import { ColDef } from "ag-grid-community";

import {
  CellRendererComponent,
  cellEditorSelector,
  getCellClassesRules,
  getCellRendererSelector,
  isEditable,
  valueFormatter,
  valueGetter,
  valueSetter,
} from "./buildColumns";

// ---------------------------------------------------------------------------
// LeafColumnBehavior — the colDef properties that vary between modes
// ---------------------------------------------------------------------------

/**
 * The subset of AG Grid ColDef properties that differ between column-build
 * modes (edit vs. comparison). Spread onto every leaf ColDef produced by the
 * 4 grouping-tier functions in buildPlanGroupColumns.ts.
 */
export type LeafColumnBehavior = Partial<ColDef>;

// ---------------------------------------------------------------------------
// ColumnBuildStrategy interface
// ---------------------------------------------------------------------------

export interface LeafColumnBehaviorBuilder {
  addParam(key: string, value: unknown): this;
  build(): LeafColumnBehavior;
}

/**
 * Defines how leaf column cell behavior is constructed.
 * Implementations control editability, rendering, and class rules without
 * touching the structural grouping tiers (1–3 tier hierarchy).
 */
export interface ColumnBuildStrategy {
  /**
   * Returns a builder for leaf column behavior. The builder pattern allows passing
   * parameters from the column-building functions (e.g. carrierColumnGroup) into the strategy without
   * the strategy needing to know about the specific column-building context or data structure.
   */
  getLeafColumnBehaviorBuilder(): LeafColumnBehaviorBuilder;

  /**
   * Returns overrides merged into the grid's defaultColDef.
   * Used to set grid-wide singleClickEdit and editable defaults per mode.
   */
  getDefaultColDefOverrides(): Partial<ColDef>;
}

// ---------------------------------------------------------------------------
// EditColumnStrategy — full edit-mode behavior (current default)
// ---------------------------------------------------------------------------

export class EditLeafColumnBehaviorBuilder implements LeafColumnBehaviorBuilder {
  private readonly strategy: ColumnBuildStrategy;

  constructor(strategy: ColumnBuildStrategy) {
    this.strategy = strategy;
  }

  addParam(_key: string, _value: unknown): this {
    return this;
  }

  build(): LeafColumnBehavior {
    if (this.strategy instanceof EditColumnStrategy) {
      return this.strategy.getLeafColumnBehavior();
    }

    return {};
  }
}

/**
 * Produces editable leaf columns with the full editor/renderer pipeline.
 * Identical to the behavior that existed before strategy extraction.
 */
export class EditColumnStrategy implements ColumnBuildStrategy {
  private readonly CellRenderer?: CellRendererComponent;

  constructor(CellRenderer?: CellRendererComponent) {
    this.CellRenderer = CellRenderer;
  }

  getLeafColumnBehaviorBuilder(): EditLeafColumnBehaviorBuilder {
    return new EditLeafColumnBehaviorBuilder(this);
  }

  getLeafColumnBehavior(): LeafColumnBehavior {
    const { CellRenderer } = this;
    return {
      editable: isEditable,
      cellEditorSelector,
      cellRendererSelector: getCellRendererSelector(CellRenderer),
      valueGetter,
      valueSetter,
      cellClassRules: getCellClassesRules(),
      valueFormatter,
    };
  }

  getDefaultColDefOverrides(): Partial<ColDef> {
    return {
      singleClickEdit: true,
      editable: true,
    };
  }
}
